import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import { FormResult } from "../../types/TeamForm";
import { getAllTeamsForm } from "../../services/scoreService";
import { useAuth } from "./authContext";

interface TeamFormContextProps {
  /** Recent form (oldest → newest) for a team id; empty array if unknown. */
  getForm: (teamId: number) => FormResult[];
  isLoading: boolean;
}

const TeamFormContext = createContext<TeamFormContextProps | undefined>(
  undefined
);

// Loads every team's recent form once (one request) and exposes a lookup by id,
// so the many fixture tiles read from cache instead of each fetching per team.
export const TeamFormProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { isAuthenticated } = useAuth();
  const [formByTeam, setFormByTeam] = useState<Map<number, FormResult[]>>(
    new Map()
  );
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // /api/Score/TeamForm requires a session; don't fetch (and drop any cache)
    // while signed out so the login page makes no unauthorized request.
    if (!isAuthenticated) {
      setFormByTeam(new Map());
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    getAllTeamsForm()
      .then((rows) => {
        if (cancelled) return;
        setFormByTeam(new Map(rows.map((row) => [row.teamId, row.results])));
      })
      // Form is non-critical decoration — on failure tiles just show "No games yet".
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  const getForm = (teamId: number): FormResult[] =>
    formByTeam.get(teamId) ?? [];

  return (
    <TeamFormContext.Provider value={{ getForm, isLoading }}>
      {children}
    </TeamFormContext.Provider>
  );
};

export const useTeamForm = (): TeamFormContextProps => {
  const context = useContext(TeamFormContext);
  if (!context) {
    throw new Error("useTeamForm must be used within a TeamFormProvider");
  }
  return context;
};
